'use client'

import { useEffect, useState, useRef } from 'react'
import Image from 'next/image'
interface StreamingProgressBarProps {
  progress: number
  isStreaming: boolean
  inChatMode?: boolean
}

const StreamingProgressBar: React.FC<StreamingProgressBarProps> = ({ 
  progress = 0, 
  isStreaming = false,
  inChatMode = false 
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const progressTextRef = useRef<HTMLDivElement>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const [displayedProgress, setDisplayedProgress] = useState(0)
  const [targetProgress, setTargetProgress] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const receivedCompleteRef = useRef(false)
  
  // Effect to update target progress when progress prop changes
  useEffect(() => {
    if (!isStreaming) return
    
    // If we've received 100%, this is the final "complete" message
    if (progress === 100) {
      receivedCompleteRef.current = true
      setTargetProgress(100)
      return
    }
    
    // Cap progress at 99% until we receive the complete message
    const newTarget = Math.min(progress, 99)
    
    // Don't go backward
    if (newTarget <= targetProgress) {
      return
    }
    
    setTargetProgress(newTarget)
  }, [progress, isStreaming, targetProgress])
  
  // Effect to start or manage the counter animation
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    // If we've reached the target, nothing to do
    if (displayedProgress >= targetProgress) {
      return
    }
        
    // Create a new interval that increments by 1 every second
    intervalRef.current = setInterval(() => {
      setDisplayedProgress(prev => {
        // Calculate next value (cap at target)
        const next = Math.min(prev + 1, targetProgress)
        
        // If we've reached the target, clear the interval
        if (next >= targetProgress && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        
        return next
      })
    }, 50) // Exactly 1 second per increment
    
    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [targetProgress, displayedProgress])
  
  // Effect to update the DOM elements when displayedProgress changes
  useEffect(() => {
    // Update the text display
    if (progressTextRef.current) {
      progressTextRef.current.textContent = `${displayedProgress}%`
    }
    
    // Update the progress bar
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${displayedProgress}%`
    }
  }, [displayedProgress])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])
  
  if (!isStreaming) return null
  
  // Different styling for in-chat mode vs full-screen
  const containerClassName = inChatMode
    ? "relative w-full h-full flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm rounded-lg shadow-md p-4"
    : "absolute inset-0 z-40 flex lg:items-center items-center justify-center lg:justify-end lg:pr-24 pr-8 bg-white/90 backdrop-blur-sm"
  
  // Different styling for content area
  const contentClassName = inChatMode
    ? "w-full flex flex-col items-center"
    : "w-[80%] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[40%] max-w-3xl flex flex-col items-center ml-4 sm:ml-8 md:ml-12 lg:ml-16 lg:mt-20"
  
  // Adjust title size for in-chat mode
  const titleClassName = inChatMode
    ? "text-xl font-bold text-[#FF6600] mb-2"
    : "text-2xl font-bold text-[#FF6600] mb-4"
  
  // Adjust description text for in-chat mode
  const descriptionClassName = inChatMode
    ? "text-gray-600 mb-4 text-center text-sm"
    : "text-gray-600 mb-8 text-center"
  
  return (
    <div 
      ref={containerRef}
      className={containerClassName}
      style={{
        position: inChatMode ? 'relative' : 'absolute',
        top: inChatMode ? 'auto' : 0,
        left: inChatMode ? 'auto' : 0,
        right: inChatMode ? 'auto' : 0,
        bottom: inChatMode ? 'auto' : 0,
        zIndex: 40
      }}
    >
      <div className={contentClassName}>
        <div className="flex items-center justify-center mb-2">
        <div className="flex items-center">
            <Image 
              src="/images/waltz-logo-orange-black.svg" 
              alt="Waltz AI" 
              width={inChatMode ? 120 : 160} 
              height={inChatMode ? 40 : 50}
              className="mb-2" 
            />
          </div>
        </div>
        <p className={descriptionClassName}>
          We are generating your content in real-time. This should only take a minute or two.
        </p>
        
        {/* Progress indicator */}
        <div className="w-full mb-2 flex justify-between items-center">
          <span className="text-gray-600 text-sm">Progress</span>
          <div ref={progressTextRef} className="text-[#FF6600] font-bold">0%</div>
        </div>
        
        {/* Progress bar container */}
        <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner">
          {/* Actual progress bar with gradient */}
          <div 
            ref={progressBarRef}
            className="progress-bar h-full rounded-full"
            style={{ 
              width: '0%',
              background: 'linear-gradient(to right, #FF7733, #FF6600)'
            }}
          ></div>
        </div>
        
        {/* Progress stages - only show in full-screen mode */}
        {!inChatMode && (
          <div className="w-full mt-8 grid grid-cols-5 gap-2">
            {[
              { stage: 'Header', percent: 20 },
              { stage: 'Hero', percent: 40 },
              { stage: 'Content', percent: 60 },
              { stage: 'Features', percent: 80 },
              { stage: 'Footer', percent: 100 },
            ].map((item, index) => (
              <div 
                key={index} 
                className={`px-3 py-2 rounded-md text-center text-sm ${
                  displayedProgress >= item.percent 
                    ? 'bg-[#FFF0E6] text-[#E65C00] border border-[#FFCBB3]' 
                    : 'bg-gray-100 text-gray-500 border border-gray-300'
                }`}
              >
                {item.stage}
              </div>
            ))}
          </div>
        )}
        
        {/* Footer text - adjust for in-chat mode */}
        <p className={`text-gray-500 ${inChatMode ? 'mt-4' : 'mt-8'} italic text-xs`}>
          Creating a high-quality page takes a little time, but it&apos;s worth the wait!
        </p>
      </div>
    </div>
  )
}

export default StreamingProgressBar